/**
 * Frontend model for the contract-detail Event Alert box.
 *
 * The backend delivers alerts via `getPortfolioIdAlertBoxesV2` as
 * `PortfolioAlertBoxDTO` (event type + CTA actions + persisted dismiss state).
 * The title/text shown for each alert is frontend-owned (i18n table keyed by
 * {@link EventClassificationEnum}); the classification is derived from the
 * contract domain + event type + actions — see `eventClassification.ts`.
 */

/**
 * Identifies which row of the spec table "Detaily chování dle kategorií smluv"
 * an alert maps to — and thus which i18n title/text is shown
 * (`Resources.EventAlert.Content[classification]`).
 */
export enum EventClassificationEnum {
	LifeInsuranceEnd = "LifeInsuranceEnd",
	LifeInsuranceAnniversary = "LifeInsuranceAnniversary",
	HousingLoanEnd = "HousingLoanEnd",
	HousingLoanFixationEnd = "HousingLoanFixationEnd",
	ConsumerLoanEnd = "ConsumerLoanEnd",
	ConsumerLoanFixationEnd = "ConsumerLoanFixationEnd",
	LoanEnd = "LoanEnd",
	PensionEnd = "PensionEnd",
	BuildingSavingsEnd = "BuildingSavingsEnd",
	InvestmentEnd = "InvestmentEnd",
	BankProductEnd = "BankProductEnd",
	BusinessLoanEnd = "BusinessLoanEnd",
	BusinessLoanFixationEnd = "BusinessLoanFixationEnd",
	RealEstateEnd = "RealEstateEnd",
	CommodityEnd = "CommodityEnd",
	ServiceEnd = "ServiceEnd",
	ServiceAnniversary = "ServiceAnniversary",
	CarInsuranceEnd = "CarInsuranceEnd",
	CarInsuranceAnniversary = "CarInsuranceAnniversary",
	PropertyInsuranceEnd = "PropertyInsuranceEnd",
	PropertyInsuranceAnniversary = "PropertyInsuranceAnniversary",
	TravelInsuranceLongEnd = "TravelInsuranceLongEnd",
	TravelInsuranceShortEnd = "TravelInsuranceShortEnd",
	AccidentHealthInsuranceEnd = "AccidentHealthInsuranceEnd",
	AccidentHealthInsuranceAnniversary = "AccidentHealthInsuranceAnniversary",
	LeasingEnd = "LeasingEnd",
	CryptocurrencyEnd = "CryptocurrencyEnd",
}

/** Visual urgency variant — orange "!" box (end/fixation) vs. blue "i" box (anniversary). */
export type EventVariant = "warning" | "info";

/** Whether the evaluated event date is in the future, today, or already past. */
export type EventTense = "future" | "today" | "past";

/** Where the navigation to the contract detail originated. */
export enum EventSourceEnum {
	/** Tap on an event in the Notification Center — bypasses server-side dismissal on load. */
	NotificationCenter = "NotificationCenter",
	Portfolio = "Portfolio",
}
